import {useEffect, useRef} from 'react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {uploadResumable} from '@/lib/resumable-upload'
import {useBusinesses, useCapabilities, type Page} from '@/hooks/empresa/organizacion/use-businesses'
import {useAllEmployees} from '@/hooks/empresa/identidad/use-employees'
import {SYSTEM_NIT} from '@/lib/constants'
import type {components} from '@/lib/api-schema'

export type BateriaEvaluacion = components['schemas']['BateriaEvaluacionRead']
export type BateriaEvaluacionEstado = components['schemas']['BateriaEvaluacionEstado']
export type BateriaEvaluacionFase = components['schemas']['BateriaEvaluacionFase']
export type BateriaEvaluacionDetail = components['schemas']['BateriaEvaluacionDetailRead']
export type BateriaCierre = components['schemas']['BateriaCierreRead']
export type BateriaCierreResumen = components['schemas']['BateriaCierreResumen']
export type BateriaPaquete = components['schemas']['BateriaPaqueteRead']
export type BateriaForma = components['schemas']['BateriaForma']
export type BateriaPaqueteEstado = components['schemas']['BateriaPaqueteEstado']
export type BateriaWorklistItem = components['schemas']['BateriaWorklistItemRead']
export type ResumenRevision = components['schemas']['ResumenRead']
export type ValidacionMasiva = components['schemas']['ValidacionMasivaRead']
export type QuitarValidacionMasiva = components['schemas']['QuitarValidacionMasivaRead']
export type PersonaCreate = components['schemas']['PersonaCreate']
export type PaqueteRetiro = components['schemas']['PaqueteRetiroRead']
export type Transcripcion = components['schemas']['TranscripcionRead']
export type TranscripcionItem = components['schemas']['TranscripcionItemRead']
type FormaEstructura = components['schemas']['FormaEstructuraRead']
export type EstructuraCuestionario = components['schemas']['EstructuraCuestionarioRead']
export type EstructuraItem = components['schemas']['EstructuraItemRead']
export type Agregados = components['schemas']['AgregadosRead']
export type AgregadoCuestionario = components['schemas']['AgregadoCuestionario']
export type NivelDistribucion = components['schemas']['NivelDistribucion']
export type Cuarentena = components['schemas']['BateriaCuarentenaRead']
export type IngestaJob = components['schemas']['BateriaIngestaJobRead']
export type Puntajes = components['schemas']['PuntajesRead']
export type PuntajeEntidad = components['schemas']['PuntajeEntidad']
export type BateriaFicha = components['schemas']['BateriaFichaRead']
export type FichaCampoMeta = components['schemas']['FichaCampoMeta']
export type FichaCampoValor = components['schemas']['FichaCampoValor']
export type FichaUpsert = components['schemas']['FichaUpsert']
export type FichaOcrRead = components['schemas']['FichaOcrRead']
type UploadTarget = components['schemas']['ScanUploadTargetRead']
export type EscaneoRegistered = components['schemas']['EscaneoRegisterRead']
export type BateriaReporteAlcance = components['schemas']['BateriaReporteAlcance']

export const EVALUACIONES_PAGE_SIZE = 10

export const base = (businessId: string) => `/admin/businesses/${businessId}/bateria`

export function useEvaluaciones(businessId: string | undefined, page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-evaluaciones', businessId, page],
    queryFn: async () =>
      (
        await api.get<Page<BateriaEvaluacion>>(`${base(businessId!)}/evaluaciones`, {
          params: {page, page_size: EVALUACIONES_PAGE_SIZE},
        })
      ).data,
    enabled: enabled && !!businessId,
    placeholderData: (previous) => previous,
  })
}

/** «Leyendo» mira la fase: la ingesta sigue viva aunque ningún paquete se haya armado todavía. */
export function useEvaluacionDetail(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['bateria-evaluacion', businessId, evaluacionId],
    queryFn: async () =>
      (await api.get<BateriaEvaluacionDetail>(`${base(businessId!)}/evaluaciones/${evaluacionId}`)).data,
    enabled: enabled && !!businessId && !!evaluacionId,
    refetchInterval: (query) => {
      const detail = query.state.data as BateriaEvaluacionDetail | undefined
      if (!detail) return false
      const zipRunning =
        detail.evaluacion.zip_formas_status === 'pendiente' || detail.evaluacion.zip_formas_status === 'procesando'
      const reporteRunning =
        detail.evaluacion.reporte_status === 'pendiente' || detail.evaluacion.reporte_status === 'procesando'
      const leyendo = detail.evaluacion.fase === 'lectura' || detail.paquetes.some((p) => p.estado === 'transcribiendo')
      return zipRunning || reporteRunning || leyendo ? 2500 : false
    },
  })
}

export function useCreateEvaluacion(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {fecha_inicio: string; psicologo_user_id: string; notas: string | null}) =>
      (await api.post<BateriaEvaluacion>(`${base(businessId)}/evaluaciones`, input)).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['bateria-evaluaciones', businessId]}),
  })
}

export function useEvaluacionInvalidation(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
}

/** Irreversible; never `entregada` (409): it is a clinical record already shared with the client. */
export function useDeleteEvaluacion(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({evaluacionId, motivo}: {evaluacionId: string; motivo: string}) => {
      await api.delete(`${base(businessId)}/evaluaciones/${evaluacionId}`, {data: {motivo}})
    },
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['bateria-evaluaciones', businessId]}),
  })
}

export function useCreatePaquetes(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (paquetes: {employee_id: string; forma: BateriaForma}[]) =>
      (await api.post<BateriaPaquete[]>(`${base(businessId)}/evaluaciones/${evaluacionId}/paquetes`, {paquetes})).data,
    onSuccess: invalidate,
  })
}

export function useCierreResumen(businessId: string | undefined, evaluacionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-cierre-resumen', businessId, evaluacionId],
    queryFn: async () =>
      (await api.get<BateriaCierreResumen>(`${base(businessId!)}/evaluaciones/${evaluacionId}/cierre-resumen`)).data,
    enabled: enabled && !!businessId && !!evaluacionId,
  })
}

export function useCerrarEvaluacion(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<BateriaCierre>(`${base(businessId)}/evaluaciones/${evaluacionId}/cerrar`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-evaluaciones', businessId]})
    },
  })
}

export function useEntregarEvaluacion(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<BateriaEvaluacion>(`${base(businessId)}/evaluaciones/${evaluacionId}/entregar`)).data,
    // Los llamadores pintan su propio aviso; sin esto la red global duplica aviso y reporte a Sentry (PYTHON-2P).
    meta: {manejaError: true},
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-evaluaciones', businessId]})
    },
  })
}

export function useStartZipFormas(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (paqueteIds: string[]) =>
      (
        await api.post<BateriaEvaluacion>(`${base(businessId)}/evaluaciones/${evaluacionId}/zip-formas`, {
          paquete_ids: paqueteIds,
        })
      ).data,
    onSuccess: invalidate,
  })
}

/** `download_url` is already a signed URL, so it opens directly, without the blob of `openFormaPdf`. */
export function useDownloadZipFormas(businessId: string, evaluacionId: string) {
  return useMutation({
    mutationFn: async () =>
      (
        await api.get<components['schemas']['ZipFormasDownloadRead']>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/zip-formas`
        )
      ).data,
    onSuccess: (data) => window.open(data.download_url, '_blank', 'noopener'),
  })
}

/** G9: `reporte_status` solo espeja el alcance consolidado. */
export function useStartReporte(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (alcance?: BateriaReporteAlcance) =>
      (
        await api.post<BateriaEvaluacion>(`${base(businessId)}/evaluaciones/${evaluacionId}/reporte`, {
          alcance: alcance ?? 'consolidado',
        })
      ).data,
    onSuccess: invalidate,
  })
}

export function useDownloadReporte(businessId: string, evaluacionId: string) {
  return useMutation({
    mutationFn: async () =>
      (
        await api.get<components['schemas']['ReporteDownloadRead']>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/reporte`
        )
      ).data,
    onSuccess: (data) => window.open(data.download_url, '_blank', 'noopener'),
  })
}

/** E3: resumable chunked upload; a lone PDF carries `paquete_id`, a ZIP the filename→paquete manifest. */
export function useUploadScan(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({
      file,
      paqueteId,
      manifest,
      onProgress,
    }: {
      file: File
      paqueteId?: string
      manifest?: Record<string, string>
      onProgress: (pct: number) => void
    }) => {
      // The backend only accepts canonical types (some browsers report `application/x-zip-compressed`);
      // mint, chunk PUT and register must share the session's Content-Type.
      const contentType = manifest !== undefined ? 'application/zip' : 'application/pdf'
      const target = (
        await api.post<UploadTarget>(`${base(businessId)}/evaluaciones/${evaluacionId}/escaneos-uploads`, {
          filename: file.name,
          content_type: contentType,
          size_bytes: file.size,
        })
      ).data
      await uploadResumable(target.session_url, file, contentType, onProgress)
      // `duplicate` (200): los mismos bytes ya se procesaron y no nace lote; se devuelve para que la UI lo diga.
      return (
        await api.post<EscaneoRegistered>(`${base(businessId)}/evaluaciones/${evaluacionId}/escaneos`, {
          object_key: target.object_key,
          content_type: contentType,
          paquete_id: paqueteId ?? null,
          manifest: manifest ?? null,
        })
      ).data
    },
    // `escaneos` also creates the ingesta job, which `useEvaluacionInvalidation` does not cover.
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['bateria-ingestas', businessId, evaluacionId]})
    },
  })
}

export function useTranscripcion(businessId: string | undefined, paqueteId: string | undefined) {
  return useQuery({
    queryKey: ['bateria-transcripcion', businessId, paqueteId],
    queryFn: async () =>
      (await api.get<Transcripcion>(`${base(businessId!)}/paquetes/${paqueteId}/transcripcion`)).data,
    enabled: !!businessId && !!paqueteId,
  })
}

/** Each field's value after the merge (saved > OMR > Kairos > empty), with its provenance. */
export function useFicha(businessId: string | undefined, paqueteId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-ficha', businessId, paqueteId],
    queryFn: async () => (await api.get<BateriaFicha>(`${base(businessId!)}/paquetes/${paqueteId}/ficha`)).data,
    enabled: enabled && !!businessId && !!paqueteId,
  })
}

/** Never mutates the employee: the value stays in this evaluación's ficha, which the demographic cuts read. */
export function useSaveFicha(businessId: string, evaluacionId: string, paqueteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (values: FichaUpsert) =>
      (await api.put<BateriaFicha>(`${base(businessId)}/paquetes/${paqueteId}/ficha`, values)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bateria-ficha', businessId, paqueteId]})
      queryClient.invalidateQueries({queryKey: ['bateria-agregados', businessId, evaluacionId]})
    },
  })
}

/** Paid model call, only on a deliberate human act (bateria-confirmacion-spec §3.8). Always 200 (failure goes in
 * `error`); seeds the cache instead of invalidating to avoid racing an in-progress edit. */
export function useFichaOcr(businessId: string, paqueteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post<FichaOcrRead>(`${base(businessId)}/paquetes/${paqueteId}/ficha-ocr`)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['bateria-ficha', businessId, paqueteId], data.ficha)
    },
  })
}

/** Inci's clinical custody. 409 until the paquete is validado, hence `retry: false`. */
export function usePuntajes(businessId: string | undefined, paqueteId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-puntajes', businessId, paqueteId],
    queryFn: async () => (await api.get<Puntajes>(`${base(businessId!)}/paquetes/${paqueteId}/puntajes`)).data,
    enabled: enabled && !!businessId && !!paqueteId,
    retry: false,
  })
}

/** Inci's clinical custody, admin-only. Throws on 409 (no canonical PDF yet). */
export async function openPdfCanonico(businessId: string, paqueteId: string): Promise<void> {
  const {download_url} = (
    await api.get<components['schemas']['PdfCanonicoRead']>(`${base(businessId)}/paquetes/${paqueteId}/pdf-canonico`)
  ).data
  window.open(download_url, '_blank', 'noopener')
}

/** Without opening it, for an embedded viewer; 409 (no canonical PDF yet) surfaces as the query error. */
export function usePdfCanonico(businessId: string | undefined, paqueteId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-pdf-canonico', businessId, paqueteId],
    queryFn: async () =>
      (
        await api.get<components['schemas']['PdfCanonicoRead']>(
          `${base(businessId!)}/paquetes/${paqueteId}/pdf-canonico`
        )
      ).data,
    enabled: enabled && !!businessId && !!paqueteId,
    retry: false,
  })
}

/** Only validated paquetes feed the aggregate. */
export function useAgregados(businessId: string | undefined, evaluacionId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-agregados', businessId, evaluacionId],
    queryFn: async () => (await api.get<Agregados>(`${base(businessId!)}/evaluaciones/${evaluacionId}/agregados`)).data,
    enabled: enabled && !!businessId && !!evaluacionId,
  })
}

/** A plain anchor can't carry the bearer token, so the PDF goes through a blob into a preview tab. */
export async function openFormaPdf(businessId: string, paqueteId: string): Promise<void> {
  const resp = await api.get(`${base(businessId)}/paquetes/${paqueteId}/forma.pdf`, {responseType: 'blob'})
  const objectUrl = URL.createObjectURL(resp.data as Blob)
  window.open(objectUrl, '_blank', 'noopener')
  // Give the new tab time to load the blob before the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

/** Item numbers only, never item text (Res. 2764/2022 copyright); static per forma. */
export function useFormaEstructura(forma: BateriaForma | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-estructura', forma],
    queryFn: async () => (await api.get<FormaEstructura>(`/admin/bateria/formas/${forma}/estructura`)).data,
    enabled: enabled && !!forma,
    staleTime: Infinity,
  })
}

/** Runs the same scoring engine and lands the paquete in `validado` with fuente=manual. */
export function useDigitacionManual(businessId: string, evaluacionId: string, paqueteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      respuestas: Record<string, string | null>
      campos_ficha: Record<string, string | null>
    }) => (await api.post<BateriaPaquete>(`${base(businessId)}/paquetes/${paqueteId}/digitacion-manual`, input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-agregados', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-transcripcion', businessId, paqueteId]})
    },
  })
}

/** En un ad-hoc la persona puede no existir en Kairos: el backend crea o reusa su Employee. */
export function useAgregarPersona(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (persona: PersonaCreate) =>
      (await api.post<BateriaPaquete>(`${base(businessId)}/evaluaciones/${evaluacionId}/personas`, persona)).data,
    onSuccess: invalidate,
  })
}

export type RetiroMotivo = components['schemas']['RetiroMotivo']

/** Con datos procesados el registro clínico permanece y solo deja de pesar en el informe.
 * G23: `motivo_detalle` solo lo exige `otro`. */
export function useRetirarPaquete(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({
      paqueteId,
      motivo,
      motivoDetalle,
    }: {
      paqueteId: string
      motivo: RetiroMotivo | null
      motivoDetalle?: string | null
    }) =>
      (
        await api.post<PaqueteRetiro>(`${base(businessId)}/paquetes/${paqueteId}/retirar`, {
          motivo,
          motivo_detalle: motivoDetalle ?? null,
        })
      ).data,
    onSuccess: invalidate,
  })
}

/** Searched by NIT: the «Sistema» business may sit past the first page. */
function useSystemBusinessId(enabled: boolean): string | undefined {
  const {data} = useBusinesses({page: 1, search: SYSTEM_NIT, sortBy: 'name', sortDir: 'asc'}, enabled)
  return data?.items.find((b) => b.nit === SYSTEM_NIT)?.id
}

/** Staff of the «Sistema» business (Inci-IPS) with a verified SST licencia; the backend enforces it too. */
export function useVerifiedPsicologos(enabled: boolean) {
  const systemBusinessId = useSystemBusinessId(enabled)
  const {data: employees} = useAllEmployees(systemBusinessId)
  // Dev only: under `licencia_dev_override` the system user acts as a licensed psicólogo so e2e can
  // create evaluaciones; the backend accepts it independently. Always false in prod.
  const devOverride = useCapabilities(systemBusinessId).sgsst_dev_override
  const eligible = (employees ?? []).filter((e) => e.licencia_estado === 'verificada' || devOverride)
  // One person can hold several employee rows.
  const byUser = new Map(eligible.map((e) => [e.user_id, e]))
  return [...byUser.values()]
}

const INGESTA_PAGE_SIZE = 20

/** Keeps polling slowly when idle: a scan can be registered out-of-band (another operator or tab). */
export function useIngestas(businessId: string | undefined, evaluacionId: string | undefined, enabled: boolean) {
  const query = useQuery({
    queryKey: ['bateria-ingestas', businessId, evaluacionId],
    queryFn: async () =>
      (
        await api.get<Page<IngestaJob>>(`${base(businessId!)}/evaluaciones/${evaluacionId}/ingestas`, {
          params: {page: 1, page_size: INGESTA_PAGE_SIZE},
        })
      ).data,
    enabled: enabled && !!businessId && !!evaluacionId,
    refetchInterval: (query) => {
      const page = query.state.data as Page<IngestaJob> | undefined
      const running = page?.items.some((j) => j.estado === 'enumerando' || j.estado === 'procesando')
      return running ? 2500 : 10_000
    },
  })
  useInvalidarAlCambiarLotes(businessId, evaluacionId, query.data)
  return query
}

/** Un lote que nace o termina mueve la fase y las hojas sin ubicar aunque no lo haya subido esta pantalla. */
function useInvalidarAlCambiarLotes(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  ingestas: Page<IngestaJob> | undefined
) {
  const queryClient = useQueryClient()
  const firma = ingestas?.items
    .map((job) => `${job.id}:${job.estado}`)
    .sort()
    .join(',')
  const vista = useRef<{evaluacion: string; firma: string} | undefined>(undefined)
  useEffect(() => {
    if (firma === undefined) return
    const evaluacion = `${businessId}/${evaluacionId}`
    const anterior = vista.current
    vista.current = {evaluacion, firma}
    // La primera respuesta de cada evaluación es el punto de partida, no un cambio.
    if (anterior?.evaluacion !== evaluacion || anterior.firma === firma) return
    queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
    queryClient.invalidateQueries({queryKey: ['bateria-revision-resumen', businessId, evaluacionId]})
  }, [firma, queryClient, businessId, evaluacionId])
}

/** Imperativo: se pide al pulsar «Ubicar sus hojas». `pageSize` es `MAX_REFS_EN_URL` (`lib/cola-filtros.ts`). */
export async function cuarentenaDeArchivo(
  businessId: string,
  evaluacionId: string,
  sourceObjectKey: string,
  pageSize: number
): Promise<Page<Cuarentena>> {
  return (
    await api.get<Page<Cuarentena>>(`${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena`, {
      params: {page: 1, page_size: pageSize, source_object_key: sourceObjectKey},
    })
  ).data
}

/** Never stops polling: a fast batch can clear `batchRunning` before this count sees its quarantined pages. */
export function useCuarentenaCount(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  enabled: boolean,
  batchRunning: boolean
) {
  const {data} = useQuery({
    queryKey: ['bateria-cuarentena-count', businessId, evaluacionId],
    queryFn: async () =>
      (
        await api.get<Page<Cuarentena>>(`${base(businessId!)}/evaluaciones/${evaluacionId}/cuarentena`, {
          params: {page: 1, page_size: 1},
        })
      ).data,
    enabled: enabled && !!businessId && !!evaluacionId,
    refetchInterval: batchRunning ? 2500 : 10_000,
  })
  return data?.total ?? 0
}

function useCuarentenaInvalidation(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['bateria-cuarentena-count', businessId, evaluacionId]})
    queryClient.invalidateQueries({queryKey: ['bateria-ingestas', businessId, evaluacionId]})
    queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
  }
}

export type Reproceso = components['schemas']['BateriaReprocesoRead']

/** Retries only pending pages, without re-uploading (`POST escaneos` would answer `duplicate`). */
export function useReprocesarCuarentena(businessId: string, evaluacionId: string) {
  const invalidate = useCuarentenaInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async () =>
      (await api.post<Reproceso>(`${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena/reprocesar`, {})).data,
    onSuccess: invalidate,
  })
}

export type Precalentamiento = components['schemas']['PrecalentamientoRead']

const precalentarKey = (businessId: string, evaluacionId: string) => ['bateria-precalentar', businessId, evaluacionId]

/** Contra la base y no el bucket: barato de pedir en cada apertura. */
export function usePrecalentamiento(businessId: string, evaluacionId: string) {
  return useQuery({
    queryKey: precalentarKey(businessId, evaluacionId),
    queryFn: async () =>
      (await api.get<Precalentamiento>(`${base(businessId)}/evaluaciones/${evaluacionId}/precalentar-recortes`)).data,
  })
}

/** La lectura ya corta los recortes; esto solo repara huecos o lotes leídos sin pre-corte. */
export function usePrecalentarRecortes(businessId: string, evaluacionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<Precalentamiento>(`${base(businessId)}/evaluaciones/${evaluacionId}/precalentar-recortes`, {}))
        .data,
    onSuccess: () => qc.invalidateQueries({queryKey: precalentarKey(businessId, evaluacionId)}),
  })
}

export function useDescartarCuarentena(businessId: string, evaluacionId: string) {
  const invalidate = useCuarentenaInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async ({cuarentenaId, motivo}: {cuarentenaId: string; motivo: string | null}) =>
      (
        await api.post<Cuarentena>(
          `${base(businessId)}/evaluaciones/${evaluacionId}/cuarentena/${cuarentenaId}/descartar`,
          {motivo}
        )
      ).data,
    onSuccess: invalidate,
  })
}

export const WORKLIST_PAGE_SIZE = 10

export type BateriaWorklistResumen = components['schemas']['BateriaWorklistResumenRead']

/** R6: contadas por el servidor sobre toda la operación abierta, nunca sumadas sobre una página. */
export function useWorklistResumen(enabled: boolean) {
  return useQuery({
    // Mismo prefijo que el worklist para heredar sus invalidaciones.
    queryKey: ['bateria-worklist', 'resumen'],
    queryFn: async () => (await api.get<BateriaWorklistResumen>('/admin/bateria/evaluaciones/resumen')).data,
    enabled,
  })
}

export type WorklistFilters = {
  fase?: BateriaEvaluacionFase
  necesitaAtencion?: boolean
  search?: string
  page: number
  pageSize?: number
}

/** P0/G7: filtro y orden los decide el servidor, nunca una segunda definición en el cliente. */
export function useWorklist(filters: WorklistFilters, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-worklist', filters],
    queryFn: async () =>
      (
        await api.get<Page<BateriaWorklistItem>>('/admin/bateria/evaluaciones', {
          params: {
            fase: filters.fase,
            necesita_atencion: filters.necesitaAtencion,
            search: filters.search || undefined,
            page: filters.page,
            page_size: filters.pageSize ?? WORKLIST_PAGE_SIZE,
          },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

/** G6: la misma cifra que usa la cola. `fase` solo activa el sondeo mientras el lote lee o revisa. */
export function useRevisionResumen(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  enabled: boolean,
  fase?: BateriaEvaluacionFase
) {
  return useQuery({
    queryKey: ['bateria-revision-resumen', businessId, evaluacionId],
    queryFn: async () =>
      (await api.get<ResumenRevision>(`${base(businessId!)}/evaluaciones/${evaluacionId}/revision/resumen`)).data,
    enabled: enabled && !!businessId && !!evaluacionId,
    refetchInterval: fase === 'lectura' || fase === 'revision' ? 5000 : false,
  })
}

/** Validar y quitar la validación del lote mueven exactamente lo mismo. */
function useValidacionLoteInvalidation(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
    queryClient.invalidateQueries({queryKey: ['bateria-agregados', businessId, evaluacionId]})
    queryClient.invalidateQueries({queryKey: ['bateria-worklist']})
    queryClient.invalidateQueries({queryKey: ['bateria-revision-resumen', businessId, evaluacionId]})
  }
}

/** G5: nunca falla en bloque; lo que no se pudo validar vuelve en `bloqueados` con su motivo. */
export function useValidarLote(businessId: string, evaluacionId: string) {
  const invalidate = useValidacionLoteInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (paqueteIds?: string[]) =>
      (
        await api.post<ValidacionMasiva>(`${base(businessId)}/evaluaciones/${evaluacionId}/validar`, {
          paquete_ids: paqueteIds ?? [],
        })
      ).data,
    // Los llamadores pintan su propio aviso; sin esto la red global duplica aviso y reporte a Sentry (PYTHON-2P).
    meta: {manejaError: true},
    onSuccess: invalidate,
  })
}

/** Nunca falla en bloque: lo que no está `validado` vuelve en `omitidas` con su motivo. */
export function useQuitarValidacionLote(businessId: string, evaluacionId: string) {
  const invalidate = useValidacionLoteInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: async (paqueteIds: string[]) =>
      (
        await api.post<QuitarValidacionMasiva>(`${base(businessId)}/evaluaciones/${evaluacionId}/quitar-validacion`, {
          paquete_ids: paqueteIds,
        })
      ).data,
    onSuccess: invalidate,
  })
}

/** G22: PDF combinado y síncrono, pedido como blob porque un ancla no lleva el token. */
async function imprimirFormasPdf(
  businessId: string,
  evaluacionId: string,
  payload: {paqueteIds?: string[]; sinImprimir?: boolean}
): Promise<{zipEncolado: boolean}> {
  const resp = await api.post(
    `${base(businessId)}/evaluaciones/${evaluacionId}/formas-pdf`,
    {paquete_ids: payload.paqueteIds ?? [], sin_imprimir: payload.sinImprimir ?? false},
    {responseType: 'blob'}
  )
  // PYTHON-34: por encima del puñado el backend encola el ZIP y responde 202; la descarga llega por su banda.
  if (resp.status === 202) return {zipEncolado: true}
  const objectUrl = URL.createObjectURL(resp.data as Blob)
  window.open(objectUrl, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  return {zipEncolado: false}
}

/** Invalidates the detail because `paquetes_sin_imprimir` shrinks after printing. */
export function useImprimirFormasPdf(businessId: string, evaluacionId: string) {
  const invalidate = useEvaluacionInvalidation(businessId, evaluacionId)
  return useMutation({
    mutationFn: (payload: {paqueteIds?: string[]; sinImprimir?: boolean}) =>
      imprimirFormasPdf(businessId, evaluacionId, payload),
    onSuccess: invalidate,
  })
}
